import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SecurityService } from 'src/app/services/security/security.service';
import { SessionGuardService } from 'src/app/services/session-guard/session-guard.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'akeela-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnInit {

    templates: any = {};
    object: any = {};
    form: any;
    submit_disabled = false;
    lang: any;
    errors: any = [];
    successMessages: any = [];
    loginSuccess = false;

    // 2FA y bloqueo de cuenta
    showSelectMethod = false;
    showVerify2FA = false;
    showAccountLocked = false;
    showSetup2FA = false;
    showPasswordRecovery = false;
    showActiveSessionDialog = false;
    tempToken = '';
    availableMethods: string[] = [];
    configuredMethods: any = {};
    twoFactorMethod: 'sms' | 'authenticator' = 'authenticator';
    phoneNumber: string | null = null;
    loginResponse: any = null;
    lockType: 'temporary' | 'permanent' = 'temporary';
    remainingTime = 0;
    incident = 1;
    unlockAt: Date | null = null;

    correoMinLength: any;
    correoMaxLength: any;
    passwordMinLength: any;
    passwordMaxLength: any;

    typeInputPass = false;

    correo: any;
    ip: any;
    password: any;

    constructor(
        private router: Router,
        private formBuilder: FormBuilder,
        public _auth: AuthService,
        private securityService: SecurityService
    ) {

    }

    ngOnInit() {
        // Verificar si ya hay una sesión activa
        if (this._auth.isAuth) {
            console.log('🔐 LOGIN: Ya hay sesión activa, redirigiendo...');
            this.router.navigate([this._auth.getDefaultRoute()]);
            return;
        }

        // Verificar si hay mensaje de sesión cerrada remotamente
        const sessionClosedMessage = localStorage.getItem('sessionClosedMessage');
        if (sessionClosedMessage) {
            this.errors = [{ msg: sessionClosedMessage }];
            localStorage.removeItem('sessionClosedMessage');
        }

        // Verificar también en sessionStorage (usado por el interceptor)
        const sessionClosedMessageSession = sessionStorage.getItem('sessionClosedMessage');
        if (sessionClosedMessageSession) {
            this.errors = [{ msg: sessionClosedMessageSession }];
            sessionStorage.removeItem('sessionClosedMessage');
        }

        this.passwordMinLength = 6;
        this.passwordMaxLength = 12;

        this.form = this.formBuilder.group({

            correo: ['', [Validators.required]],
            password: ['', [Validators.required, Validators.minLength(this.passwordMinLength), Validators.maxLength(this.passwordMaxLength)]]

        });
    }


    // Función para manejar Enter key
    onEnterKey(): void {
        console.log('🔑 Enter key pressed from input - calling submit()');
        this.submit();
    }

    // HostListener para capturar Enter a nivel de componente
    // Solo actúa cuando NO hay modales de 2FA abiertos
    @HostListener('keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            // No interceptar Enter si hay un modal/vista de 2FA abierto
            if (this.showVerify2FA || this.showSetup2FA || this.showSelectMethod || this.showActiveSessionDialog || this.showPasswordRecovery || this.showAccountLocked) {
                return; // Dejar que el formulario del modal maneje el Enter
            }
            console.log('🎯 Enter captured by HostListener');
            event.preventDefault();
            this.submit();
        }
    }

    private isProcessing = false;

    async submit(): Promise<void> {
        console.clear();
        console.log('🚀 Submit method called');

        // Prevenir múltiples llamadas simultáneas
        if (this.isProcessing) {
            console.log('⚠️ Login ya está en proceso, ignorando submit duplicado');
            return;
        }

        this.errors = [];
        this.successMessages = [];
        if (this.form.valid) {
            this.isProcessing = true;
            this.submit_disabled = true;
            this.password = this.form.get('password').value;

            const body = {
                username: this.form.get('correo').value,
                password: this.form.get('password').value,
                forceLogin: false,
            }

            try {
                let resp = await this._auth.login(body);
                console.log('🔐 Login response:', resp);

                // Verificar si requiere 2FA (usuario YA tiene 2FA activado)
                if (resp.requiresTwoFactor && resp.tempToken) {
                    console.log('✅ Usuario requiere verificación 2FA');
                    console.log('🔐 Métodos disponibles:', resp.availableMethods);
                    console.log('🔐 Métodos configurados:', resp.configuredMethods);
                    this.tempToken = resp.tempToken;
                    this.availableMethods = resp.availableMethods || [];
                    this.configuredMethods = resp.configuredMethods || {};
                    this.phoneNumber = resp.phone || null;

                    // Mostrar selector de método
                    this.showSelectMethod = true;
                    this.submit_disabled = false;
                    this.isProcessing = false;
                    return;
                }

                // Verificar si requiere SETUP de 2FA (primer login, sin 2FA configurado)
                if (resp.requiresSetup && resp.tempToken) {
                    console.log('✅ Usuario requiere configurar 2FA por primera vez');
                    console.log('🔐 Métodos disponibles para configurar:', resp.availableMethods);
                    this.tempToken = resp.tempToken;
                    this.availableMethods = resp.availableMethods || [];
                    this.configuredMethods = {}; // Ninguno configurado aún

                    // Mostrar selector de método para que elija cuál configurar
                    this.showSelectMethod = true;
                    this.submit_disabled = false;
                    this.isProcessing = false;
                    return;
                }

                // Login exitoso directo (trusted device o sin 2FA)
                console.log('✅ Login exitoso directo');
                if (resp.sessionId) {
                    SessionGuardService.saveSessionId(resp.sessionId);
                }
                await this._auth.setAuth({ token: resp.token });
                await new Promise(resolve => setTimeout(resolve, 100));

                const redirectRoute = this._auth.getDefaultRoute();
                console.log('✅ Redirigiendo a:', redirectRoute);
                window.location.href = redirectRoute;
                this.submit_disabled = false;

            } catch (error: any) {
                // Manejar cuenta bloqueada
                if (error?.status === 423 && error?.error?.locked) {
                    this.handleAccountLocked(error.error);
                    this.submit_disabled = false;
                    this.isProcessing = false;
                    return;
                }

                // Manejar sesión activa (error 409) — mostrar diálogo de confirmación
                if (error?.status === 409 && error?.error?.activeSession) {
                    this.showActiveSessionDialog = true;
                    this.submit_disabled = false;
                    this.isProcessing = false;
                    return;
                }

                // Manejar usuario sin rol RBAC asignado
                if (error?.status === 403 && error?.error?.code === 'NO_RBAC_ROLE') {
                    this.errors = [{ msg: error.error.msg }];
                    this.submit_disabled = false;
                    this.isProcessing = false;
                    return;
                }

                // Manejar otros errores - consolidar en un solo mensaje
                let errorMsg = error?.error?.msg || 'Usuario o contraseña incorrectos';

                // Agregar intentos restantes al mismo mensaje si están disponibles
                if (error?.error?.attemptsRemaining !== undefined) {
                    errorMsg += `. Intentos restantes: ${error.error.attemptsRemaining}`;
                }

                this.errors = [{ msg: errorMsg }];

                console.error(error);
                this.submit_disabled = false;
                this.isProcessing = false;
            }
        } else {
            console.log('❌ Form is not valid:', this.form.errors);
        }
    }

    handleAccountLocked(lockData: any): void {
        this.lockType = lockData.lockType;
        this.remainingTime = lockData.remainingTime || 0;
        this.incident = lockData.incident || 1;
        this.unlockAt = lockData.unlockAt ? new Date(lockData.unlockAt) : null;

        // Mostrar alerta usando el sistema de errors del componente
        if (lockData.lockType === 'permanent') {
            this.errors = [{ msg: 'Tu cuenta ha sido bloqueada permanentemente. Por favor, contacta al soporte.' }];
        } else {
            const minutes = Math.ceil(this.remainingTime / 60);
            const incidentMsg = this.incident === 1 ? 'Primera incidencia' : this.incident === 2 ? 'Segunda incidencia' : 'Tercera incidencia';
            this.errors = [{ msg: `Cuenta bloqueada por ${minutes} minutos. ${incidentMsg}. Después de 3 incidencias, tu cuenta será bloqueada permanentemente.` }];
        }

        // NO mostrar el modal
        // this.showAccountLocked = true;
    }

    async on2FAVerified(data: any): Promise<void> {
        console.log('🔐 2FA verificado exitosamente');
        this.showVerify2FA = false;
        this.loginSuccess = true;

        // Guardar device token si el backend lo devolvió
        if (data.deviceToken) {
            console.log('💾 [LOGIN] Guardando device token en localStorage');
            localStorage.setItem('kredit_device_token', data.deviceToken);
        }

        // Guardar sessionId para el session guard
        if (data.sessionId) {
            SessionGuardService.saveSessionId(data.sessionId);
        }

        // Guardar el token y cargar permisos RBAC
        await this._auth.setAuth(data);

        // Esperar un momento para asegurar sincronización
        await new Promise(resolve => setTimeout(resolve, 100));

        // Usar window.location.href para forzar recarga completa
        // Esto asegura que toda la aplicación se reinicie con el nuevo estado de autenticación
        const redirectRoute = this._auth.getDefaultRoute();
        console.log('🔐 Redirigiendo a:', redirectRoute);
        window.location.href = redirectRoute;
    }

    on2FACancelled(): void {
        this.showVerify2FA = false;
        this.tempToken = '';
        this.errors = [{ msg: 'Verificación 2FA cancelada. Por favor, inicia sesión nuevamente.' }];
    }

    onAccountLockedClose(): void {
        this.showAccountLocked = false;
        if (this.lockType === 'temporary' && this.remainingTime <= 0) {
            this.errors = [{ msg: 'Tu cuenta ha sido desbloqueada. Puedes intentar iniciar sesión nuevamente.' }];
        }
    }

    async onSetup2FAComplete(data: any): Promise<void> {
        console.log('✅ Setup 2FA completado');
        console.log('💾 Data recibido:', data);
        this.showSetup2FA = false;
        this.loginSuccess = true;

        // Si viene token del setup SMS, usarlo directamente
        if (data && data.token) {
            console.log('🔑 Usando token del setup SMS');
            if (data.sessionId) {
                SessionGuardService.saveSessionId(data.sessionId);
            }
            await this._auth.setAuth({ token: data.token });

            // Esperar sincronización
            await new Promise(resolve => setTimeout(resolve, 100));

            console.log('🔑 Redirigiendo después de setup SMS');
            window.location.href = environment.redirectRoute;
        } else if (this.loginResponse) {
            // Si es Google Authenticator, usar loginResponse guardado
            console.log('🔑 Usando loginResponse guardado');
            await this._auth.setAuth(this.loginResponse);

            // Esperar sincronización
            await new Promise(resolve => setTimeout(resolve, 100));

            console.log('🔑 Redirigiendo después de setup Authenticator');
            window.location.href = environment.redirectRoute;
        } else {
            console.warn('⚠️ No hay token disponible, recargando página');
            window.location.reload();
        }
    }

    onSetup2FASkip(): void {
        console.log('⏭️ Usuario canceló setup 2FA - regresando al selector');
        this.showSetup2FA = false;
        this.showSelectMethod = true;
    }

    onMethodSelected(method: string): void {
        console.log('🔐 Método seleccionado:', method);
        this.twoFactorMethod = method as 'sms' | 'authenticator';

        // Verificar si el método está configurado
        const isConfigured = this.configuredMethods[method];
        console.log(`📋 Método ${method} configurado:`, isConfigured);

        if (isConfigured) {
            // Método ya configurado - ir a verificación
            this.showSelectMethod = false;
            this.showVerify2FA = true;
        } else {
            // Método NO configurado - ir a setup con método preseleccionado
            console.log('⚙️ Método no configurado - mostrando setup para:', method);
            this.showSelectMethod = false;
            this.showSetup2FA = true;
            // Guardar loginResponse para aplicar después del setup
            this.loginResponse = { token: this.tempToken };
        }
    }

    onSetupRequired(method: string): void {
        console.log('🔧 Setup requerido para método:', method);
        // Ocultar selector y mostrar modal de setup con método preseleccionado
        this.showSelectMethod = false;
        this.showSetup2FA = true;
        this.twoFactorMethod = method as 'sms' | 'authenticator';
    }

    onMethodSelectionCancelled(): void {
        console.log('❌ Selección de método cancelada');
        this.showSelectMethod = false;
        this.tempToken = '';
        this.availableMethods = [];
        this.errors = [{ msg: 'Verificación 2FA cancelada. Por favor, inicia sesión nuevamente.' }];
    }

    onPasswordRecoveryCancel(): void {
        this.showPasswordRecovery = false;
    }

    onPasswordRecoverySuccess(): void {
        this.showPasswordRecovery = false;
        this.errors = [];
        this.successMessages = [{ msg: 'Contraseña cambiada exitosamente. Por favor, inicia sesión con tu nueva contraseña.' }];
    }

    // === Sesión activa detectada ===
    onActiveSessionCancel(): void {
        this.showActiveSessionDialog = false;
    }

    async onForceLogin(): Promise<void> {
        this.showActiveSessionDialog = false;
        this.submit_disabled = true;
        this.errors = [];

        try {
            const body = {
                username: this.form.get('correo').value,
                password: this.form.get('password').value,
                forceLogin: true,
            };

            let resp = await this._auth.login(body);
            console.log('🔐 Force login response:', resp);

            // Verificar si requiere 2FA
            if (resp.requiresTwoFactor && resp.tempToken) {
                this.tempToken = resp.tempToken;
                this.availableMethods = resp.availableMethods || [];
                this.configuredMethods = resp.configuredMethods || {};
                this.phoneNumber = resp.phone || null;
                this.showSelectMethod = true;
                this.submit_disabled = false;
                return;
            }

            if (resp.requiresSetup && resp.tempToken) {
                this.tempToken = resp.tempToken;
                this.availableMethods = resp.availableMethods || [];
                this.configuredMethods = {};
                this.showSelectMethod = true;
                this.submit_disabled = false;
                return;
            }

            // Login exitoso directo (trusted device o sin 2FA)
            if (resp.token) {
                if (resp.sessionId) {
                    SessionGuardService.saveSessionId(resp.sessionId);
                }
                await this._auth.setAuth({ token: resp.token });
                await new Promise(resolve => setTimeout(resolve, 100));
                const redirectRoute = this._auth.getDefaultRoute();
                console.log('✅ Force login redirigiendo a:', redirectRoute);
                window.location.href = redirectRoute;
            }
            this.submit_disabled = false;
        } catch (error: any) {
            if (error?.status === 423 && error?.error?.locked) {
                this.handleAccountLocked(error.error);
            } else {
                this.errors = [{ msg: error?.error?.msg || 'Error al iniciar sesión' }];
            }
            this.submit_disabled = false;
        }
    }

}